import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Api from '../../service/http';

const FinalConfirmation = () => {
  const { theme } = useSelector((state) => state.theme);
  const navigate = useNavigate();
  const { id } = useParams();
  const [submitting, setSubmitting] = useState(false);

  // The Api helper never rejects on 4xx/5xx, so the outcome must be read from
  // res.status and res.data — otherwise an expired link, a provider refusal or
  // a server error would still be reported to the user as a success.
  const handleUnsubscribe = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await Api.post(`/web/cancel-subscription/${id}`);
      const data = res?.data ?? {};

      if (res?.status === 401) {
        alert('Your session has expired. Please sign in again and reopen the cancellation link.');
        navigate(`/signin?redirect=/unsubscribe/${id}`);
        return;
      }

      if (res?.status !== 200 || data.error) {
        alert(
          data.message ||
            'We could not cancel the subscription. Please try again, or contact support if this keeps happening.'
        );
        return;
      }

      alert(
        data.message ||
          'Your subscription has been cancelled. You will receive a confirmation email shortly.'
      );
      navigate('/profile');
    } catch (error) {
      alert('Unsubscription failed. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`flex-1 flex flex-col items-center justify-center p-4 sm:p-6 min-h-[calc(100vh-150px)] ${theme === 'dark' ? 'bg-slate-900 text-slate-200' : 'bg-slate-50 text-slate-900'}`}>
      <div className={`w-full max-w-xl ${theme === 'dark' ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-slate-100'} backdrop-blur-xl rounded-[2rem] p-6 sm:p-10 shadow-2xl border`}>
        <div className="flex flex-col items-center text-center space-y-4 mb-8">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center text-red-600 text-3xl animate-bounce">
            ⚠️
          </div>
          <h2 className={`text-2xl sm:text-3xl font-bold tracking-tight ${theme === 'dark' ? 'text-slate-100' : 'text-slate-900'}`}>
            Final Confirmation
          </h2>
        </div>

        <div className="space-y-8">
          <p className={`${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'} text-base sm:text-lg text-center leading-relaxed`}>
            You are about to <span className="font-bold text-red-600">cancel</span> your subscription.
            It will not renew again, and you keep access until the end of the period you have already paid for.
          </p>

          <div className={`${theme === 'dark' ? 'bg-red-900/20 text-red-300 border-red-900/50' : 'bg-red-50 text-red-700 border-red-100'} border-2 border-dashed px-6 py-4 rounded-2xl text-sm font-bold text-center uppercase tracking-widest`}>
            To come back later you will need to subscribe again.
          </div>

          <div className="flex flex-col gap-4 pt-4">
            <button
              onClick={handleUnsubscribe}
              disabled={submitting}
              className="w-full py-5 bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm uppercase tracking-widest rounded-2xl shadow-[0_20px_50px_rgba(220,_38,_38,_0.3)] hover:shadow-[0_20px_50px_rgba(220,_38,_38,_0.5)] transition-all duration-300 active:scale-95"
            >
              {submitting ? 'Cancelling…' : 'Confirm Unsubscription'}
            </button>
            <button
              onClick={() => navigate('/')}
              className={`w-full py-4 font-bold text-xs uppercase tracking-widest rounded-2xl transition-all duration-300 active:scale-95 border-2 ${
                theme === 'dark' 
                  ? 'bg-slate-800 text-slate-200 border-slate-700 hover:bg-slate-700' 
                  : 'bg-white text-slate-600 border-slate-100 hover:bg-slate-50 shadow-sm'
              }`}
            >
              No, Keep My Subscription
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalConfirmation;
